/*
 * AMBIENT LIFE: what the dashboard does while the site is quiet (2026-10-08).
 *
 * Designed and approved as a mock (design-prototypes/empty-state-motion/settings-and-greeting.html and
 * idle-life-2.html); this is the same thing in the real dashboard. Read those for the WHY of each look.
 *
 * ── WHAT IT DRAWS, AND WHERE ────────────────────────────────────────────────────────────────────────────
 *   UNDER the page boxes, inside the SVG, between the grey panel and the boxes (so no box, label or number
 *   is ever tinted or covered):
 *     one <canvas> in a <foreignObject>: the sky tint, a soft glow around each box, and a ghost layer
 *     (rising bubbles, drifting ghost bubbles, birds or a kite).
 *   OVER the boxes, inside the SVG:
 *     standby lights on the boxes' corners, and a ring around each historical dock bubble.
 *   OUTSIDE the SVG, as CSS driven by classes on <body> (ambientLife.scss): the ring and caption on the
 *     visitor counter, and a ring on the header's traffic dot. The greeting is a React component.
 *
 * ── THE RULE THAT MATTERS MOST: IT MEANS "QUIET AND CONNECTED", NEVER JUST "QUIET" ──────────────────────
 *   TrafficIndicator exists because "nothing on screen distinguishes a dead pipe from a quiet afternoon".
 *   A glow that keeps breathing while the dashboard's socket is DOWN would paint exactly that lie. So the
 *   layers run only while the socket is up AND nobody is onsite AND the view is live and in grid mode:
 *     - someone is onsite   -> RECEDE: fade to a faint level, then stop (the real bubbles own the screen)
 *     - socket down         -> OFF: fade away completely
 *     - playback / single view -> OFF
 *   After the site goes quiet again it waits RESUME_DELAY_MS of continuous quiet before it comes back, so
 *   a visitor leaving and the next one arriving seconds later does not make the glow strobe.
 *
 * ── COST, STATED RATHER THAN HOPED ──────────────────────────────────────────────────────────────────────
 *   One canvas at ~0.6 of CSS resolution (everything on it is soft), drawn at 30fps, only while the layers
 *   are visibly alive or fading. When they have settled the loop STOPS (no rAF at all). The lights and dock
 *   rings are small CSS animations (transform/opacity only, never a filter: perf note 2) and are PAUSED
 *   when settled. Nothing here runs per D3 tick and nothing touches the bubbles.
 *   NOT measured: the cost inside the real dashboard on a real screen.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ────────────────────────────────────────────────────────────────────
 *   The mock also had the dock bubbles breathing and drifting. The real dock bubbles are D3-positioned,
 *   draggable, and carry separate icon images; scaling the shapes would fight the drag and leave the icons
 *   behind, so only a ring is drawn around them.
 *   The header dot stays the colour of its own state. TrafficIndicator reserves motion for "events are
 *   arriving", so a quiet dot gets a faint RING in its own grey, never the live green.
 */
import { eventEmitter } from "../eventEmitters/EventEmitters";
import * as playbackModeService from "./playbackModeService";
import { AMBIENT_DEFAULTS, AmbientSettings, sanitizeAmbient } from "./ambientSettings";
import { drawKite } from "./ambientKite";
import { drawFisherman, fishermanPlan, fishermanScript, FishermanScript, FISHERMAN_ABOVE, FISHERMAN_BELOW, FISHERMAN_STOP_MAX, FISHERMAN_STOP_MIN, FISHERMAN_SURF } from "./ambientFisherman";
import { drawSailboats, makeSailVisit, sailboatsPlan, SailVisit, SAILBOATS_ABOVE, SAILBOATS_BELOW, SAILBOATS_SURF } from "./ambientSailboats";
import "./ambientLife.scss";

export interface AmbientStatus {
  connected: boolean;
  onsite: number;
}
export type AmbientPhase = "off" | "active" | "receding";
type Listener = (active: boolean) => void;

const SVG_NS = "http://www.w3.org/2000/svg";
const XHTML_NS = "http://www.w3.org/1999/xhtml";

/** how long the site must stay continuously quiet before the layers come back (ms) */
const RESUME_DELAY_MS = 4000;
/** the very first appearance after the dashboard opens can be quicker: there was nothing to strobe against */
const FIRST_DELAY_MS = 1200;
const POLL_MS = 500;
/** fade time constant (s); about three of these is the 1.4s fade the mock uses */
const FADE_TAU = 0.45;
/** canvas backing resolution relative to CSS pixels: the content is soft, and fewer pixels is the cost lever */
const RES = 0.6;
const FRAME_MS = 33;
/** how faint each layer is while visitors are on the site (the mock's values) */
const RECEDED = { sky: 0.15, glow: 0.12, ghost: 0.2 };
/** the replica's page boxes were 104 wide; ghost sizes scale by (real box width / that) */
const MOCK_BOX_W = 104;

const PALETTE = ["#dd3f4e", "#4fb2b7", "#89b06a", "#845e9f", "#f6ba22", "#0593c0"];

const GLOWS: Record<string, { colour: number[]; peak: number }> = {
  bio: { colour: [64, 214, 200], peak: 0.2 },
  moon: { colour: [118, 168, 232], peak: 0.17 },
  wind: { colour: [137, 176, 106], peak: 0.36 },   // 0.2 was too faint to see (Chris)
};

/* the sky tint over a day (card A of the time-of-day sheet): hour, colour, opacity at the top, at the bottom */
const KEYS: [number, number[], number, number][] = [
  [0, [150, 168, 222], 0.105, 0.045],
  [5, [165, 170, 226], 0.105, 0.045],
  [6.5, [255, 190, 175], 0.15, 0.06],
  [9, [255, 232, 184], 0.12, 0.03],
  [12, [255, 250, 232], 0.15, 0.04],
  [15, [255, 226, 180], 0.12, 0.03],
  [18.5, [255, 178, 160], 0.165, 0.07],
  [20, [186, 170, 236], 0.13, 0.05],
  [22, [150, 168, 222], 0.105, 0.045],
  [24, [150, 168, 222], 0.105, 0.045],
];

interface Box { x: number; y: number; w: number; h: number }
interface Panel { x: number; y: number; w: number; h: number }

/* ───────── module state ───────── */
let config: AmbientSettings = AMBIENT_DEFAULTS;
let statusProvider: (() => AmbientStatus) | null = null;
let singleView = false;
let started = false;
let phase: AmbientPhase = "off";
let everActive = false;
let quietSince = 0;
const listeners = new Set<Listener>();
let pollTimer: number | null = null;
let minuteTimer: number | null = null;
let resizeTimer: number | null = null;

let svgEl: SVGSVGElement | null = null;
let underG: SVGGElement | null = null;
let overG: SVGGElement | null = null;
let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let panel: Panel | null = null;
let boxes: Box[] = [];
let ppu = 1;
let dockTimer: number | null = null;

let raf = 0;
let lastTs = 0;
let lastDraw = 0;
let clock = 0;
const k = { sky: 0, glow: 0, ghost: 0, bar: 0 };
let model: { glowKey: string; ghostKey: string; glow: any[]; ghost: any } = { glowKey: "", ghostKey: "", glow: [], ghost: null };

const reducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ───────── small helpers ───────── */
function rng(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rgba = (c: number[], a: number) => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a.toFixed(3)})`;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const tri = (p: number) => { p -= Math.floor(p); return p < 0.5 ? p * 2 : (1 - p) * 2; };
const smooth = (x: number) => x * x * (3 - 2 * x);
/** CSS's ease-in-out between two keyframes, close enough to the smoothstep */
const easeBetween = (a: number, b: number, t: number) => lerp(a, b, smooth(Math.min(1, Math.max(0, t))));

/** piecewise keyframes [[position 0..1, value], ...] with ease-in-out between each pair */
function keyframes(frames: number[][], p: number) {
  p -= Math.floor(p);
  for (let i = 0; i < frames.length - 1; i++) {
    const a = frames[i], b = frames[i + 1];
    if (p >= a[0] && p <= b[0]) return easeBetween(a[1], b[1], b[0] === a[0] ? 1 : (p - a[0]) / (b[0] - a[0]));
  }
  return frames[frames.length - 1][1];
}

function skyAt(hour: number) {
  const h = ((hour % 24) + 24) % 24;
  for (let i = 0; i < KEYS.length - 1; i++) {
    const a = KEYS[i], b = KEYS[i + 1];
    if (h >= a[0] && h <= b[0]) {
      const t = (h - a[0]) / (b[0] - a[0]);
      return { c: [lerp(a[1][0], b[1][0], t), lerp(a[1][1], b[1][1], t), lerp(a[1][2], b[1][2], t)], top: lerp(a[2], b[2], t), bot: lerp(a[3], b[3], t) };
    }
  }
  return { c: KEYS[0][1], top: KEYS[0][2], bot: KEYS[0][3] };
}
const nowHour = () => { const d = new Date(); return d.getHours() + d.getMinutes() / 60; };

/* ───────── the phase machine: when does it run? ───────── */
function readStatus(): AmbientStatus {
  try {
    return statusProvider ? statusProvider() : { connected: false, onsite: 0 };
  } catch {
    return { connected: false, onsite: 0 };
  }
}

/**
 * The grey panel (#maincluster) can change size AFTER the layers were built (the grid settles, a row is added, the window is
 * resized by something other than a window resize), and the layers were sized once from its attributes: the glow, the sky and
 * the drifting layer then stopped short of the panel's real bottom edge (reported by Chris). So its size is compared on every
 * poll, and the layers are re-fitted when it differs.
 */
function syncPanel() {
  const grey = document.getElementById("maincluster");
  if (!grey || !panel) return;
  const px = parseFloat(grey.getAttribute("x") || "0"), py = parseFloat(grey.getAttribute("y") || "0");
  const pw = parseFloat(grey.getAttribute("width") || "0"), ph = parseFloat(grey.getAttribute("height") || "0");
  if (!(pw > 0 && ph > 0)) return;
  if (Math.abs(px - panel.x) < 0.5 && Math.abs(py - panel.y) < 0.5 && Math.abs(pw - panel.w) < 0.5 && Math.abs(ph - panel.h) < 0.5) return;
  if (readGeometry()) { sizeCanvas(); model.glowKey = ""; model.ghostKey = ""; refreshDockRings(); kick(); }
}

function evaluate() {
  if (!started) return;
  syncPanel();
  const live = playbackModeService.mode === "live";
  const st = readStatus();
  const grid = !singleView;
  const surfaceReady = !!(panel && canvas);
  let target: AmbientPhase;
  if (!live || !st.connected || !grid || !surfaceReady) target = "off";
  else if (st.onsite > 0) target = "receding";
  else target = "active";

  const now = performance.now();
  if (target === "active") {
    if (phase === "active") { quietSince = 0; return; }
    if (!quietSince) quietSince = now;
    if (now - quietSince < (everActive ? RESUME_DELAY_MS : FIRST_DELAY_MS)) return;
    quietSince = 0;
  } else {
    quietSince = 0;
    if (phase === target) return;
  }
  setPhase(target);
}

function setPhase(next: AmbientPhase) {
  if (next === phase) return;
  const wasActive = phase === "active";
  phase = next;
  if (next === "active") everActive = true;
  applyBodyClasses();
  applyOverClasses();
  kick();
  if (wasActive !== (next === "active")) listeners.forEach((fn) => fn(next === "active"));
}

function applyBodyClasses() {
  const b = document.body;
  b.classList.toggle("ambient-active", phase === "active");
  b.classList.toggle("ambient-counter-on", phase === "active" && config.ambientCounter);
}

function applyOverClasses() {
  if (!overG) return;
  overG.classList.toggle("ambient-receding", phase === "receding");
  overG.classList.toggle("ambient-off", phase === "off");
  overG.classList.toggle("ambient-settled", phase !== "active");
  overG.classList.toggle("ambient-lights-on", false);   // the standby lights ("fireflies") were removed at Chris's request: never shown, whatever an account holds
  overG.classList.toggle("ambient-dock-on", config.ambientDock);
}

/* ───────── geometry, read from the real SVG ───────── */
function readGeometry(): boolean {
  const svg = document.querySelector("#main_svg svg.svg-main-window") as SVGSVGElement | null;
  const grey = document.getElementById("maincluster");
  if (!svg || !grey) return false;
  const px = parseFloat(grey.getAttribute("x") || "0");
  const py = parseFloat(grey.getAttribute("y") || "0");
  const pw = parseFloat(grey.getAttribute("width") || "0");
  const ph = parseFloat(grey.getAttribute("height") || "0");
  if (!(pw > 0 && ph > 0)) return false;
  svgEl = svg;
  panel = { x: px, y: py, w: pw, h: ph };
  const vb = svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width ? svg.viewBox.baseVal.width : 0;
  const cssW = svg.getBoundingClientRect().width;
  ppu = vb > 0 && cssW > 0 ? cssW / vb : 1;
  readBoxes();
  return true;
}

/*
 * The page boxes, as a list plus a signature. This is NOT a build-time fact: a slot only becomes a page box
 * (selected="true") when a page is ASSIGNED to it, and that happens after the grid is built, when the page list
 * arrives (and again whenever someone assigns or removes a page). The first version read the boxes once, at build,
 * and so saw only "All other pages" (always a page box): the first screenshot from staging showed standby lights on
 * that one box and none on the other eleven, and the glow had the same flaw. A state that changes after a build
 * must be watched or re-derived, never snapshotted: see watchBoxes().
 */
let boxSig = "";
let lightsSig = "";
function readBoxes() {
  const svg = svgEl;
  if (!svg) return false;
  /* a page box is any slot rect that holds a page ("selected") plus "All other pages"; an empty slot is plain grey */
  boxes = [];
  svg.querySelectorAll("g > rect").forEach((r) => {
    // d3 sets the attribute as "rectIndex" (an SVG element keeps its case), but a node built from parsed markup
    // would hold "rectindex"; read either so a casing difference can never silently empty the glow
    const idx = r.getAttribute("rectIndex") ?? r.getAttribute("rectindex");
    if (idx === null) return;   // not a slot rect (the flow-lines layer and others also hold rects)
    const isPage = r.getAttribute("selected") === "true" || idx === "12";
    if (!isPage) return;
    const w = parseFloat(r.getAttribute("width") || "0"), h = parseFloat(r.getAttribute("height") || "0");
    if (w > 0 && h > 0) boxes.push({ x: parseFloat(r.getAttribute("x") || "0"), y: parseFloat(r.getAttribute("y") || "0"), w, h });
  });
  const sig = boxes.map((b) => `${b.x},${b.y},${b.w},${b.h}`).join("|");
  const changed = sig !== boxSig;
  boxSig = sig;
  /*
   * EVERY reader of the boxes brings the things built FROM them into line, here, in the one place that sees the
   * change. The first fix did it in refreshBoxes() only, and staging proved that was not enough: after a rebuild a
   * second "late geometry pass" (onRebuild, onResize) also calls this, updated the signature quietly, and then
   * refreshBoxes() found nothing changed and never relit the lights. The glow keyed on the count and rebuilt, so it
   * worked; the lights did not. Two writers of one derived state, and only one of them knew about its dependants.
   */
  if (changed && overG) {
    syncLights();
    if (started) kick();
  }
  return true;
}

/** the lights are built FROM the box signature, so they are rebuilt whenever it differs from the one they were built for */
function syncLights() {
  if (!overG || lightsSig === boxSig) return;
  overG.querySelector("g.ambient-lights")?.remove();
  buildLights();
}

/** a page was assigned to, or removed from, a slot: relight the boxes that are pages now */
let boxObserver: MutationObserver | null = null;
let boxTimer: number | null = null;
function watchBoxes() {
  unwatchBoxes();
  if (!svgEl || typeof MutationObserver === "undefined") return;
  boxObserver = new MutationObserver(() => {
    // twelve slots are assigned in one go; settle for a moment so that is one rebuild, not twelve
    if (boxTimer) window.clearTimeout(boxTimer);
    boxTimer = window.setTimeout(() => { boxTimer = null; refreshBoxes(); }, 80);
  });
  boxObserver.observe(svgEl, { attributes: true, attributeFilter: ["selected"], subtree: true });
}
function unwatchBoxes() {
  boxObserver?.disconnect();
  boxObserver = null;
  if (boxTimer) { window.clearTimeout(boxTimer); boxTimer = null; }
}
function refreshBoxes() {
  if (!started || !overG) return;
  readBoxes();      // relights and redraws if (and only if) the boxes differ from what is built
  syncLights();     // belt and braces: also covers a signature some other reader already moved on
}

/* ───────── the layers inside the SVG ───────── */
function removeLayers() {
  underG?.remove();
  overG?.remove();
  underG = overG = null;
  canvas = null;
  ctx = null;
  if (dockTimer) { window.clearInterval(dockTimer); dockTimer = null; }
  unwatchBoxes();
}

function buildLayers() {
  removeLayers();
  if (!readGeometry() || !svgEl || !panel) return;
  const grey = document.getElementById("maincluster");
  if (!grey || !grey.parentNode) return;

  /* UNDER: right after the grey panel, so every slot group (appended later) paints above it */
  underG = document.createElementNS(SVG_NS, "g") as SVGGElement;
  underG.setAttribute("id", "ambient-under");
  underG.setAttribute("pointer-events", "none");
  const fo = document.createElementNS(SVG_NS, "foreignObject");
  fo.setAttribute("x", String(panel.x));
  fo.setAttribute("y", String(panel.y));
  fo.setAttribute("width", String(panel.w));
  fo.setAttribute("height", String(panel.h));
  (fo as SVGElement).style.pointerEvents = "none";
  (fo as SVGElement).style.overflow = "hidden";
  canvas = document.createElementNS(XHTML_NS, "canvas") as HTMLCanvasElement;
  canvas.style.cssText = "display:block;width:100%;height:100%;pointer-events:none;";
  fo.appendChild(canvas);
  underG.appendChild(fo);
  grey.parentNode.insertBefore(underG, grey.nextSibling);

  /* OVER: after the slot groups, before any bubble D3 appends later */
  overG = document.createElementNS(SVG_NS, "g") as SVGGElement;
  overG.setAttribute("id", "ambient-over");
  overG.setAttribute("pointer-events", "none");
  svgEl.appendChild(overG);

  sizeCanvas();
  buildLights();
  refreshDockRings();
  watchBoxes();
  // the dock bubbles MOVE, so their rings are re-read on a timer; the same tick is a safety net for the box watcher
  dockTimer = window.setInterval(() => { refreshDockRings(); refreshBoxes(); }, 2500);
  model = { glowKey: "", ghostKey: "", glow: [], ghost: null };
  applyOverClasses();
  applyBodyClasses();
  kick();
}

function sizeCanvas() {
  if (!canvas || !panel) return;
  // the foreignObject that holds the canvas is fitted to the panel too: it was sized once at build and then left, so a panel
  // that changed size afterwards left the layers short of (or past) its real bottom edge
  const fo = canvas.parentNode as SVGElement | null;
  if (fo && fo.setAttribute) {
    fo.setAttribute("x", String(panel.x)); fo.setAttribute("y", String(panel.y));
    fo.setAttribute("width", String(panel.w)); fo.setAttribute("height", String(panel.h));
  }
  const cssW = panel.w * ppu, cssH = panel.h * ppu;
  const w = Math.max(2, Math.min(2200, Math.round(cssW * RES)));
  const h = Math.max(2, Math.min(1400, Math.round(cssH * RES)));
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  ctx = canvas.getContext("2d");
}

function buildLights() {
  if (!overG) return;
  const lights = document.createElementNS(SVG_NS, "g");
  lights.setAttribute("class", "ambient-lights");
  const r = rng(77);
  boxes.forEach((b) => {
    const t = 2.4 + r() * 1.8, d = -r() * t;
    const cx = b.x + b.w - 10, cy = b.y + 8;
    const ring = document.createElementNS(SVG_NS, "circle");
    ring.setAttribute("class", "ambient-led-ring");
    ring.setAttribute("cx", String(cx)); ring.setAttribute("cy", String(cy)); ring.setAttribute("r", "2.5");
    (ring as SVGElement).style.cssText = `--t:${t.toFixed(2)}s;--d:${d.toFixed(2)}s`;
    const core = document.createElementNS(SVG_NS, "circle");
    core.setAttribute("class", "ambient-led");
    core.setAttribute("cx", String(cx)); core.setAttribute("cy", String(cy)); core.setAttribute("r", "2.5");
    (core as SVGElement).style.cssText = `--t:${t.toFixed(2)}s;--d:${d.toFixed(2)}s`;
    lights.appendChild(ring);
    lights.appendChild(core);
  });
  overG.appendChild(lights);
  lightsSig = boxSig;
}

/*
 * In ICON mode a historical dock shape is filled "transparent" (the icon image is what you see), and the ring used to copy that fill as its stroke: a ring in "transparent" is in the DOM and
 * can never be seen (Chris, 2026-10-14: "Ambient life doesn't show historical ripples on the icons"). The category colour then comes from the shape's own bound datum, through a resolver
 * clusterService registers (this file cannot import clusterService: it imports this one).
 */
let dockColourOf: (datum: any) => string = () => "";
export function setDockColourResolver(fn: (datum: any) => string) { dockColourOf = fn; }
const DOCK_DEFAULT_COLOUR = "#14477d";
const isVisibleColour = (c: string) => !!c && !/^(none|transparent)$/i.test(c.trim()) && !/^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)$/i.test(c.trim());

/** a ring around every historical dock bubble, in that bubble's own colour; refreshed because dock bubbles move */
function refreshDockRings() {
  if (!overG || !svgEl) return;
  if (!config.ambientDock) {
    overG.querySelector("g.ambient-dock")?.remove();   // switched off: leave no rings behind
    return;
  }
  let g = overG.querySelector("g.ambient-dock") as SVGGElement | null;
  if (!g) {
    g = document.createElementNS(SVG_NS, "g") as SVGGElement;
    g.setAttribute("class", "ambient-dock");
    overG.appendChild(g);
  }
  const els = svgEl.querySelectorAll("circle[historical=true], rect[historical=true]");
  const want: { cx: number; cy: number; r: number; c: string }[] = [];
  els.forEach((el) => {
    let cx: number, cy: number, r: number;
    if (el.tagName.toLowerCase() === "circle") {
      cx = parseFloat(el.getAttribute("cx") || "NaN"); cy = parseFloat(el.getAttribute("cy") || "NaN"); r = parseFloat(el.getAttribute("r") || "NaN");
    } else {
      const w = parseFloat(el.getAttribute("width") || "NaN"), h = parseFloat(el.getAttribute("height") || "NaN");
      cx = parseFloat(el.getAttribute("x") || "NaN") + w / 2; cy = parseFloat(el.getAttribute("y") || "NaN") + h / 2; r = Math.max(w, h) / 2;
    }
    if (![cx, cy, r].every((n) => isFinite(n)) || r <= 0) return;
    const fill = (el as SVGElement).style.fill || el.getAttribute("fill") || "";
    let colour = isVisibleColour(fill) ? fill : "";
    if (!colour) {
      try { colour = dockColourOf((el as any).__data__) || ""; } catch (e) { colour = ""; }
    }
    want.push({ cx, cy, r, c: isVisibleColour(colour) ? colour : DOCK_DEFAULT_COLOUR });
  });
  /* reuse the ring elements instead of remove+recreate (perf note 3): only touch what changed */
  while (g.childElementCount > want.length) g.lastElementChild?.remove();
  while (g.childElementCount < want.length) {
    const ring = document.createElementNS(SVG_NS, "circle");
    ring.setAttribute("class", "ambient-dock-ring");
    (ring as SVGElement).style.setProperty("--d", `${(-Math.random() * 4).toFixed(2)}s`);
    g.appendChild(ring);
  }
  want.forEach((w, i) => {
    const ring = g!.children[i] as SVGElement;
    ring.setAttribute("cx", w.cx.toFixed(1));
    ring.setAttribute("cy", w.cy.toFixed(1));
    ring.setAttribute("r", w.r.toFixed(1));
    ring.style.stroke = w.c;
    ring.style.setProperty("--ring-scale", String(Math.min(2.6, (w.r + 26) / w.r)));
  });
}

/* ───────── the models: glow and ghost, rebuilt only when the choice or the boxes change ───────── */
function buildGlowModel(kind: string) {
  const r = rng(21), out: any[] = [];
  if (!panel) return out;
  boxes.forEach((b) => {
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
    if (kind === "bio") {
      out.push({ t: "halo", cx, cy, w: b.w * 1.7, h: b.h * 2.4, c: GLOWS.bio.colour, a: GLOWS.bio.peak, T: 7 + r() * 4, ph: r(), lo: 0.15 });
      out.push({ t: "halo", cx, cy, w: b.w * 1.24, h: b.h * 1.7, c: [120, 240, 225], a: GLOWS.bio.peak * 1.2, T: 4.5 + r() * 3, ph: r(), lo: 0.05 });
    } else if (kind === "moon") {
      out.push({ t: "patchA", cx, cy, w: b.w * 1.9, h: b.h * 2.6, c: GLOWS.moon.colour, a: GLOWS.moon.peak, T: 15 + r() * 8, ph: r() });
      out.push({ t: "patchB", cx, cy, w: b.w * 1.4, h: b.h * 2.0, c: [210, 228, 250], a: GLOWS.moon.peak * 1.1, T: (15 + r() * 8) * 1.3, ph: r() });
    } else if (kind === "wind") {
      const p = ((cx - panel!.x) / panel!.w) * 0.8 + ((cy - panel!.y) / panel!.h) * 0.2;
      out.push({ t: "gust", cx, cy, w: b.w * 1.7, h: b.h * 2.4, c: GLOWS.wind.colour, a: GLOWS.wind.peak, delay: p * 8 });
    }
  });
  return out;
}

function boxScale() {
  // the NARROWEST page box is the ordinary slot ("All other pages" spans three columns). Using the first box read
  // 400px while only that wide box was a page yet, which blew the ghost bubbles up to several times their size.
  const w = boxes.length ? Math.min(...boxes.map((b) => b.w)) : 150;
  return Math.max(0.6, w / MOCK_BOX_W);
}

function buildGhostModel(kind: string) {
  if (!panel || kind === "none") return null;
  const W = panel.w, H = panel.h, s = boxScale(), r = rng(91), m: any = { kind, items: [] };
  if (kind === "rise") {
    for (let i = 0; i < 24; i++) m.items.push({ x: r() * W, y: r() * H, r: (10 + r() * 34) * 0.55 * s, c: PALETTE[Math.floor(r() * PALETTE.length)], vx: (r() - 0.5) * 9 * s, vy: -(3 + r() * 7) * s, a: 0.1 + r() * 0.12 });
  } else if (kind === "drift") {
    for (let j = 0; j < 14; j++) m.items.push({ x: r() * W, y: 10 * s + r() * (H - 20 * s), r: (4 + r() * 5) * s, vx: (8 + r() * 14) * s * (r() > 0.25 ? 1 : -1), ph: r() * 6.28, bob: (3 + r() * 5) * s, a: 0.12 + r() * 0.1 });
  } else if (kind === "birds") {
    for (let f = 0; f < 3; f++) {
      const dir = f % 2 ? -1 : 1, size = [1, 0.7, 0.5][f] * s, count = [7, 9, 11][f];
      const flock: any = { dir, size, y0: H * [0.3, 0.55, 0.2][f], speed: [30, 22, 16][f] * s, x: dir > 0 ? W * (0.04 + 0.38 * f) : W * (0.96 - 0.3 * f), a: [0.26, 0.2, 0.15][f], birds: [] };
      for (let q = 0; q < count; q++) {
        const side = q % 2 ? 1 : -1, row = Math.ceil(q / 2);
        flock.birds.push({ dx: -dir * row * 15 * size, dy: side * row * 8 * size + (r() - 0.5) * 4 * size, ph: r() * 6.28, sp: 5 + r() * 2 });
      }
      m.items.push(flock);
    }
  }
  return m;
}

function ensureModels() {
  if (model.glowKey !== `${config.ambientGlow}|${boxSig}`) {
    model.glowKey = `${config.ambientGlow}|${boxSig}`;
    model.glow = config.ambientGlow === "none" ? [] : buildGlowModel(config.ambientGlow);
  }
  if (model.ghostKey !== `${config.ambientGhost}|${panel?.w}|${panel?.h}|${Math.round(boxScale() * 100)}`) {
    model.ghostKey = `${config.ambientGhost}|${panel?.w}|${panel?.h}|${Math.round(boxScale() * 100)}`;
    model.ghost = buildGhostModel(config.ambientGhost);
  }
}

/* ───────── drawing ───────── */
function halo(c2: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number, scale: number, colour: number[], peak: number, alpha: number) {
  if (alpha <= 0.002) return;
  c2.save();
  c2.translate(cx, cy);
  c2.scale((w / 2) * scale, (h / 2) * scale);
  const g = c2.createRadialGradient(0, 0, 0, 0, 0, 1);
  g.addColorStop(0, rgba(colour, peak));
  g.addColorStop(0.42, rgba(colour, peak * 0.55));
  g.addColorStop(0.72, rgba(colour, peak * 0.16));
  g.addColorStop(1, rgba(colour, 0));
  c2.globalAlpha = alpha;
  c2.fillStyle = g;
  c2.fillRect(-1, -1, 2, 2);
  c2.restore();
}

function drawGlow(c2: CanvasRenderingContext2D, t: number, kGlow: number) {
  model.glow.forEach((e) => {
    if (e.t === "halo") {
      const ease = smooth(tri(t / e.T + e.ph));
      halo(c2, e.cx, e.cy, e.w, e.h, 0.95 + 0.12 * ease, e.c, e.a, (e.lo + (1 - e.lo) * ease) * kGlow);
    } else if (e.t === "fly") {
      const wob = (t / e.W + e.wph);
      const dx = keyframes([[0, 0], [0.33, 9], [0.66, -7], [1, 0]], wob);
      const dy = keyframes([[0, 0], [0.33, -6], [0.66, 5], [1, 0]], wob);
      const blink = keyframes([[0, 0], [0.06, 1], [0.13, 0.12], [0.21, 0.95], [0.34, 0], [0.62, 0], [1, 0]], t / e.T + e.ph);
      if (blink * kGlow <= 0.003) return;
      c2.save();
      c2.translate(e.bx + dx, e.by + dy);
      c2.scale(17, 17);
      const g = c2.createRadialGradient(0, 0, 0, 0, 0, 1);
      g.addColorStop(0, "rgba(255,244,150,0.95)");
      g.addColorStop(0.22, "rgba(214,232,80,0.55)");
      g.addColorStop(0.6, "rgba(190,220,70,0.14)");
      g.addColorStop(1, "rgba(190,220,70,0)");
      c2.globalAlpha = blink * kGlow;
      c2.fillStyle = g;
      c2.fillRect(-1, -1, 2, 2);
      c2.restore();
    } else if (e.t === "patchA" || e.t === "patchB") {
      const p = smooth(tri(t / e.T + e.ph));
      const sx = e.t === "patchA" ? lerp(-0.09, 0.09, p) : lerp(0.08, -0.08, p);
      const sy = e.t === "patchA" ? lerp(0.03, -0.03, p) : lerp(-0.04, 0.04, p);
      halo(c2, e.cx + sx * e.w, e.cy + sy * e.h, e.w, e.h, 1, e.c, e.a, kGlow);
    } else if (e.t === "gust") {
      const p = (t - e.delay) / 15;
      const pulse = keyframes([[0, 0.04], [0.24, 1], [0.52, 0.04], [1, 0.04]], p);
      const sc = keyframes([[0, 0.96], [0.24, 1.08], [0.52, 0.96], [1, 0.96]], p);
      halo(c2, e.cx, e.cy, e.w, e.h, sc, e.c, e.a, pulse * kGlow);
    }
  });
}

function stepGhost(m: any, dt: number) {
  if (!panel) return;
  const W = panel.w, H = panel.h;
  if (m.kind === "rise") {
    m.items.forEach((b: any) => {
      b.x += b.vx * dt; b.y += b.vy * dt;
      if (b.y + b.r < 0) { b.y = H + b.r; b.x = Math.random() * W; }
      if (b.x < -b.r) b.x = W + b.r;
      if (b.x > W + b.r) b.x = -b.r;
    });
  } else if (m.kind === "drift") {
    m.items.forEach((b: any) => {
      b.x += b.vx * dt;
      if (b.vx > 0 && b.x - b.r > W) b.x = -b.r;
      if (b.vx < 0 && b.x + b.r < 0) b.x = W + b.r;
    });
  } else if (m.kind === "birds") {
    m.items.forEach((f: any) => {
      f.x += f.dir * f.speed * dt;
      if ((f.dir > 0 && f.x > W * 1.5) || (f.dir < 0 && f.x < -W * 0.5)) f.x = f.dir > 0 ? -W * 0.2 : W * 1.2;
    });
  }
}

function drawGhost(c2: CanvasRenderingContext2D, m: any, t: number, kGhost: number) {
  if (!m || kGhost <= 0.002 || !panel) return;
  const W = panel.w, s = boxScale();
  if (m.kind === "rise") {
    m.items.forEach((b: any) => { c2.globalAlpha = b.a * kGhost; c2.fillStyle = b.c; c2.beginPath(); c2.arc(b.x, b.y, b.r, 0, 6.2832); c2.fill(); });
  } else if (m.kind === "drift") {
    m.items.forEach((b: any) => {
      const y = b.y + Math.sin(t * 0.9 + b.ph) * b.bob;
      c2.globalAlpha = b.a * kGhost; c2.fillStyle = "#7f9ab8"; c2.beginPath(); c2.arc(b.x, y, b.r, 0, 6.2832); c2.fill();
      c2.globalAlpha = Math.min(1, b.a * 1.4) * kGhost; c2.strokeStyle = "#6f8aa8"; c2.lineWidth = 0.8 * s; c2.stroke();
    });
  } else if (m.kind === "kite") {
    // the kite is an analytic function of the clock: nothing to step, nothing to keep (ambientKite.ts)
    drawKite(c2, panel.w, panel.h, t + 2, 0.92 * kGhost);
  } else if (m.kind === "birds") {
    m.items.forEach((f: any) => {
      const yy = f.y0 + Math.sin(f.x / (W * 0.18)) * 14 * f.size;
      c2.strokeStyle = "#566b80"; c2.lineWidth = 1.5 * f.size; c2.lineCap = "round"; c2.globalAlpha = f.a * kGhost;
      f.birds.forEach((b: any) => {
        const bx = f.x + b.dx, by = yy + b.dy, L = 8 * f.size, sn = Math.sin(t * b.sp + b.ph);
        c2.beginPath(); c2.moveTo(bx - L, by - L * 0.55 * sn); c2.quadraticCurveTo(bx - L * 0.4, by - L * 0.85 * (0.3 + 0.7 * sn), bx, by);
        c2.quadraticCurveTo(bx + L * 0.4, by - L * 0.85 * (0.3 + 0.7 * sn), bx + L, by - L * 0.55 * sn); c2.stroke();
      });
    });
  }
  c2.globalAlpha = 1;
}

function draw() {
  if (!ctx || !canvas || !panel) return;
  ensureModels();
  const c2 = ctx;
  c2.setTransform(1, 0, 0, 1, 0, 0);
  c2.clearRect(0, 0, canvas.width, canvas.height);
  // draw in the panel's own user units, from its top-left corner
  c2.setTransform(canvas.width / panel.w, 0, 0, canvas.height / panel.h, 0, 0);
  if (k.sky > 0.002 && config.ambientTimeLight) {
    const s = skyAt(nowHour());
    const grad = c2.createLinearGradient(0, 0, 0, panel.h);
    grad.addColorStop(0, rgba(s.c, s.top * k.sky));
    grad.addColorStop(1, rgba(s.c, s.bot * k.sky));
    c2.fillStyle = grad;
    c2.fillRect(0, 0, panel.w, panel.h);
  }
  if (config.ambientGlow !== "none" && k.glow > 0.002) drawGlow(c2, clock, k.glow);
  if (config.ambientGhost !== "none" && k.ghost > 0.002) drawGhost(c2, model.ghost, clock, k.ghost);
}


/* ───────── ALONG THE BOTTOM BAR: a canvas laid over the top edge of the footer ─────────
 *
 * The bottom metrics bar is an ordinary page element outside the SVG, so what moves along it cannot live in the SVG's
 * canvas. This is a second canvas, fixed over the footer's top edge, taking no clicks (pointer-events:none), under the drawer
 * and the scrub bar (z-index 2; the footer itself is 1). It exists only while "Along the bottom bar" is switched on, and it
 * follows exactly the same rule as every other layer here: quiet AND connected, fade on a visitor, off in playback and the
 * single view, nothing at all under reduced motion.
 *
 * The footer's own top edge is the SURFACE of the sea. NOTHING is ever drawn over a number: the boat floats above them, and
 * everything is clipped to the gap between the footer's top edge and its first line of text (drawFisherman's maxBelow). The
 * canvas follows the footer when the footer is collapsed (and then there is no sea, so nothing is drawn).
 *
 * FISHERMAN: he rows in from a random side as far as the middle of the page or a little past it, and then fishes for as long as
 * the site stays quiet (Chris: "he enjoys fishing and can do it for a long time"); a visitor arriving fades him, and the next
 * quiet spell starts again from a random side.
 *
 * SAILBOATS: two small boats on routes rolled from a seed (straight across, in and turned round, or a zigzag). A visit lasts until
 * the last boat has gone, then the sea is empty for a while (SAIL_GAP_MIN..MAX seconds), then a new visit with a new seed begins,
 * for as long as the site stays quiet. A visitor arriving fades them, and the next quiet spell starts with a fresh visit.
 *
 * Only one of them runs at a time: the choice is a single setting, and changing it starts the new thing from the beginning.
 */
const BAR_GEO_MS = 250;
interface BarGeo { left: number; top: number; width: number; freeL: number; freeR: number; padTop: number }
let barCanvas: HTMLCanvasElement | null = null;
let barCtx: CanvasRenderingContext2D | null = null;
let barClock = 0;
let barFirstDir: 1 | -1 = 1;
let barStop = 0.55;   // how far across the page he rows (a fraction of its width)
let barGeo: BarGeo | null = null;
let barGeoAt = 0;
let barVisit: { key: string; script: FishermanScript | null; dir: number; u: number } = { key: "", script: null, dir: 0, u: 0 };
let barShown = false;
type BarKind = "none" | "fisherman" | "sailboats";
let barKind: BarKind = "none";   // what the canvas is running now; a change restarts the visit
let barRolled = false;           // true once the next visit has been rolled and has not started yet (so it is rolled once, not every frame)
let sailVisit: SailVisit | null = null;
let sailEnd = 0;                 // seconds after which the sailboats' visit (and the empty sea that follows it) is over
const SAIL_GAP_MIN = 12, SAIL_GAP_MAX = 40;

function readBarGeometry(): BarGeo | null {
  const f = document.querySelector(".footer-main") as HTMLElement | null;
  if (!f || f.classList.contains("footer-hide-main")) return null;
  const r = f.getBoundingClientRect();
  if (!(r.width > 0 && r.height > 10) || r.top >= window.innerHeight - 8) return null;
  const items = f.querySelectorAll(".footer-list-align > li");
  if (!items.length) return null;
  const first = items[0].getBoundingClientRect(), last = items[items.length - 1].getBoundingClientRect();
  if (!(first.width > 0 && last.width > 0)) return null;
  // how far below the footer's top edge the first line of text starts: nothing of his may go deeper than that
  let textTop = Infinity;
  f.querySelectorAll(".footer-list-align h4, .footer-list-align h6, .footer-list-align p").forEach((e) => { const b = e.getBoundingClientRect(); if (b.height > 0) textTop = Math.min(textTop, b.top); });
  const padTop = isFinite(textTop) ? Math.min(40, Math.max(0, textTop - r.top)) : 8;
  return { left: r.left, top: r.top, width: r.width, freeL: first.left - r.left, freeR: r.right - last.right, padTop };
}

function removeBarCanvas() {
  if (barCanvas) barCanvas.remove();
  barCanvas = null; barCtx = null; barGeo = null; barShown = false; barClock = 0;
  barVisit = { key: "", script: null, dir: 0, u: 0 };
  sailVisit = null; sailEnd = 0; barRolled = false; barKind = "none";
}
/** roll the next visit of whatever is on the bar (once, while nothing is showing) */
function rollBarVisit() {
  if (barRolled) return;
  barRolled = true;
  if (barKind === "sailboats") {
    sailVisit = makeSailVisit(1 + Math.floor(Math.random() * 99999));
    sailEnd = sailVisit.end + SAIL_GAP_MIN + Math.random() * (SAIL_GAP_MAX - SAIL_GAP_MIN);
    return;
  }
  barFirstDir = Math.random() < 0.5 ? 1 : -1;
  barStop = FISHERMAN_STOP_MIN + Math.random() * (FISHERMAN_STOP_MAX - FISHERMAN_STOP_MIN);
}

/** the bar canvas exists exactly while the service is running AND something is chosen for the bar */
function ensureBarCanvas() {
  const kind: BarKind = started ? config.ambientBar : "none";
  if (kind !== barKind) {   // a different choice: start the new thing from the beginning, with nothing of the old one left over
    barKind = kind; barClock = 0; barRolled = false; sailVisit = null; sailEnd = 0;
    barVisit = { key: "", script: null, dir: 0, u: 0 };
    clearBar();
  }
  const want = kind !== "none";
  if (!want) { removeBarCanvas(); return; }
  if (barCanvas) return;
  const c = document.createElement("canvas");
  c.id = "ambient-bar";
  c.setAttribute("aria-hidden", "true");
  c.style.cssText = "position:fixed;left:0;top:0;pointer-events:none;z-index:2;display:none;";
  document.body.appendChild(c);
  barCanvas = c;
  barCtx = c.getContext("2d");
}

function clearBar() {
  if (!barCanvas || !barShown) return;
  if (barCtx) { barCtx.setTransform(1, 0, 0, 1, 0, 0); barCtx.clearRect(0, 0, barCanvas.width, barCanvas.height); }
  barCanvas.style.display = "none";
  barShown = false;
}

function drawBar() {
  if (!barCanvas || !barCtx) return;
  if (k.bar <= 0.002) { clearBar(); return; }
  const nowMs = performance.now();
  if (!barGeo || nowMs - barGeoAt > BAR_GEO_MS) { barGeo = readBarGeometry(); barGeoAt = nowMs; }
  const g = barGeo;
  if (!g) { clearBar(); return; }
  // what is on the bar decides how tall the canvas must be, the scale of its units, and what is painted into it
  let A: number, B: number, u: number, surf: number, paint: (c: CanvasRenderingContext2D, pw: number, ph: number, maxBelow: number) => void;
  if (barKind === "sailboats") {
    const plan = sailboatsPlan(g.width), visit = sailVisit;
    if (!plan || !visit) { clearBar(); return; }
    A = SAILBOATS_ABOVE * plan.u; B = SAILBOATS_BELOW * plan.u; u = plan.u; surf = SAILBOATS_SURF;
    paint = (c, pw, ph, maxBelow) => drawSailboats(c, barClock, { boats: visit.boats, alpha: k.bar, cw: pw, ch: ph, x0: 0, x1: plan.width, maxBelow, xs: plan.width / 1000 });
  } else {
    const plan = fishermanPlan(g.width, barStop), side = barFirstDir > 0 ? plan.left : plan.right, dir = barFirstDir;
    if (!side) { clearBar(); return; }
    const key = `${dir}|${side.u.toFixed(3)}|${side.xStop.toFixed(1)}|${side.width.toFixed(1)}`;
    if (barVisit.key !== key) barVisit = { key, script: fishermanScript(dir, { width: side.width, xStop: side.xStop }), dir, u: side.u };
    const S = barVisit.script as FishermanScript;
    // the canvas: the footer's width, from far enough above its top edge to hold the rod, to just inside it for the reflection
    A = FISHERMAN_ABOVE * plan.u0; B = FISHERMAN_BELOW * plan.u0; u = side.u; surf = FISHERMAN_SURF;
    paint = (c, pw, ph, maxBelow) => drawFisherman(c, barClock, { S, alpha: k.bar, cw: pw, ch: ph, x0: 0, x1: side.width, maxBelow });
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = g.width, cssH = A + B, pw = Math.max(2, Math.round(cssW * dpr)), ph = Math.max(2, Math.round(cssH * dpr));
  if (barCanvas.width !== pw) barCanvas.width = pw;
  if (barCanvas.height !== ph) barCanvas.height = ph;
  const st = barCanvas.style;
  st.left = `${g.left}px`; st.top = `${g.top - A}px`; st.width = `${cssW}px`; st.height = `${cssH}px`; st.display = "block";
  const c = barCtx;
  c.setTransform(1, 0, 0, 1, 0, 0); c.clearRect(0, 0, pw, ph);
  // stage units -> device pixels, with the sea surface on the footer's top edge (A px down this canvas)
  c.setTransform(dpr * u, 0, 0, dpr * u, 0, dpr * (A - surf * u));
  // the sea may reach down to 2px short of the first line of text, and never deeper
  const maxBelow = Math.max(0, (g.padTop - 2) / u);
  paint(c, pw, ph, maxBelow);
  barShown = true;
}

/* ───────── the loop ───────── */
function targets() {
  if (phase === "active") return { sky: 1, glow: 1, ghost: 1, bar: config.ambientBar !== "none" ? 1 : 0 };
  if (phase === "receding") return { sky: RECEDED.sky, glow: RECEDED.glow, ghost: 0, bar: 0 };
  return { sky: 0, glow: 0, ghost: 0, bar: 0 };
}

function atTargets() {
  const t = targets();
  return Math.abs(k.sky - t.sky) < 0.003 && Math.abs(k.glow - t.glow) < 0.003 && Math.abs(k.ghost - t.ghost) < 0.003 && Math.abs(k.bar - t.bar) < 0.003;
}

function frame(now: number) {
  raf = 0;
  if (!ctx || !panel) return;
  if (now - lastDraw < FRAME_MS) { raf = requestAnimationFrame(frame); return; }
  const dt = lastTs ? Math.min(0.1, (now - lastTs) / 1000) : 0;
  lastTs = now; lastDraw = now;
  const t = targets(), a = 1 - Math.exp(-dt / FADE_TAU);
  k.sky += (t.sky - k.sky) * a; k.glow += (t.glow - k.glow) * a; k.ghost += (t.ghost - k.ghost) * a; k.bar += (t.bar - k.bar) * a;
  clock += dt;
  // the bottom bar's own clock runs while its scene is visible and starts over once it is gone, so each quiet spell begins at the beginning
  // (the boat rowing in, the sailboats coming on). The sailboats' visit also ends by itself: after the last boat has gone and a spell of empty
  // sea, a new visit with a new seed begins.
  if (k.bar > 0.002) { barClock += dt; barRolled = false; } else { barClock = 0; rollBarVisit(); }
  // (also when the choice was switched to sailboats while the bar was already showing: there is no visit yet, so start one now)
  if (barKind === "sailboats" && (!sailVisit || barClock > sailEnd)) { barClock = 0; rollBarVisit(); }
  // the ghost layer keeps drifting while it fades out, and is frozen (then cleared) once it has
  if (model.ghost && k.ghost > 0.002) stepGhost(model.ghost, dt);
  draw();
  drawBar();
  if (atTargets() && phase !== "active") {
    /* settled: snap to the exact targets, draw the final still frame, and STOP. No rAF runs from here. */
    k.sky = t.sky; k.glow = t.glow; k.ghost = t.ghost; k.bar = t.bar;
    draw();
    drawBar();
    lastTs = 0;
    return;
  }
  raf = requestAnimationFrame(frame);
}

/** (re)start the loop if there is anything to animate */
function kick() {
  if (!ctx || !panel) return;
  if (reducedMotion()) {
    /* no movement at all: the tint is the one thing that still shows, drawn once, and again each minute */
    const t = targets();
    k.sky = t.sky; k.glow = 0; k.ghost = 0; k.bar = 0;
    draw();
    drawBar();
    return;
  }
  if (!raf) { lastTs = 0; raf = requestAnimationFrame(frame); }
}

/* ───────── public API ───────── */
export function start(options: { getStatus: () => AmbientStatus }) {
  if (started) stop();
  started = true;
  statusProvider = options.getStatus;
  eventEmitter.addListener("createStaticComponents", onRebuild);
  eventEmitter.addListener("modeChanged", onModeChanged);
  window.addEventListener("resize", onResize);
  pollTimer = window.setInterval(evaluate, POLL_MS);
  minuteTimer = window.setInterval(() => { if (!raf) draw(); }, 60000);
  buildLayers();
  ensureBarCanvas();
  evaluate();
}

export function stop() {
  started = false;
  if (raf) { cancelAnimationFrame(raf); raf = 0; }
  if (pollTimer) { window.clearInterval(pollTimer); pollTimer = null; }
  if (minuteTimer) { window.clearInterval(minuteTimer); minuteTimer = null; }
  if (resizeTimer) { window.clearTimeout(resizeTimer); resizeTimer = null; }
  eventEmitter.removeListener("createStaticComponents", onRebuild);
  eventEmitter.removeListener("modeChanged", onModeChanged);
  window.removeEventListener("resize", onResize);
  removeLayers();
  removeBarCanvas();
  const wasActive = phase === "active";
  phase = "off"; quietSince = 0; everActive = false;
  k.sky = k.glow = k.ghost = k.bar = 0;
  document.body.classList.remove("ambient-active", "ambient-counter-on");
  if (wasActive) listeners.forEach((fn) => fn(false));
}

export function setConfig(next: any) {
  const clean = sanitizeAmbient(next);
  const changed = JSON.stringify(clean) !== JSON.stringify(config);
  config = clean;
  if (!changed) return;
  model = { glowKey: "", ghostKey: "", glow: [], ghost: null };
  applyBodyClasses();
  applyOverClasses();
  if (started) { ensureBarCanvas(); refreshDockRings(); evaluate(); kick(); if (!raf) { draw(); drawBar(); } }
}

export function getConfig(): AmbientSettings { return config; }
export function isActive(): boolean { return phase === "active"; }
export function getPhase(): AmbientPhase { return phase; }

/** the clone/click-through view ("single cluster") hides the page boxes, so there is nothing for a glow to sit behind */
export function setSingleView(value: boolean) {
  if (singleView === value) return;
  singleView = value;
  if (started) evaluate();
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function onRebuild() {
  /* the whole SVG was rebuilt, so the layers went with it. Rebuild on the next frame: the panel is sized by now,
     and a second pass catches a late resize. */
  buildLayers();
  window.requestAnimationFrame(() => { if (started) { readGeometry(); sizeCanvas(); } });
  evaluate();
}

function onModeChanged() { evaluate(); }

function onResize() {
  if (resizeTimer) window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    resizeTimer = null;
    if (!started) return;
    barGeoAt = 0;   // the footer may have moved or changed width: read it again on the next frame
    if (readGeometry()) { sizeCanvas(); model.glowKey = ""; model.ghostKey = ""; refreshDockRings(); kick(); if (!raf) { draw(); drawBar(); } }
  }, 300);
}

/** for the test page and for measuring: a plain snapshot, no side effects */
export function debugState() {
  return {
    phase, started, quietSince, everActive,
    k: { ...k },
    running: !!raf,
    hasCanvas: !!canvas,
    canvasSize: canvas ? [canvas.width, canvas.height] : null,
    panel, boxes: boxes.length, ppu, singleView,
    glowElements: model.glow.length, ghostKind: model.ghost ? model.ghost.kind : null,
    ghostRadii: model.ghost && model.ghost.kind === "rise" ? model.ghost.items.map((i: any) => i.r) : [],
    lights: overG ? overG.querySelectorAll(".ambient-led").length : 0,
    dockRings: overG ? overG.querySelectorAll(".ambient-dock-ring").length : 0,
    bar: {
      canvas: !!barCanvas, k: k.bar, clock: barClock, shown: barShown, geo: barGeo, dir: barVisit.dir, u: barVisit.u, kind: barKind,
      sail: sailVisit ? { boats: sailVisit.boats.length, end: sailEnd, visit: sailVisit.end } : null,
    },
    config,
  };
}
